import { SignInPageContainer } from '@pique/component/auth-components';
import { AuthService } from '@pique/core/modules/cloud';
import { useLiveData, useService } from '@toeverything/infra';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { authAtom } from '../atoms';
import { AuthPanel } from '../components/pique/auth';
import { RouteLogic, useNavigateHelper } from '../hooks/use-navigate-helper';

async function syncSessionToTauriVault() {
  const match = document.cookie
    .split('; ')
    .find(c => c.startsWith('pique_session='));
  if (!match) return;
  const sessionId = match.split('=')[1];
  if (!sessionId) return;

  const { commands: authCommands } = await import(
    '../tauri/plugins/auth'
  );

  const res = await fetch('/api/auth/session', {
    headers: { Authorization: `Bearer ${sessionId}` },
  });
  const data = await res.json();
  if (data?.user?.id) {
    await authCommands.setInStore('auth-user-id', data.user.id);
    await authCommands.initVault(data.user.id);
  }
  await authCommands.setInVault('remote-server', sessionId);
  const { refreshTauriAuthToken } = await import(
    '../modules/cloud/utils/tauri-auth'
  );
  await refreshTauriAuthToken();
  console.log('[sign-in] synced session cookie to Tauri vault');
}

export const SignIn = () => {
  const [{ state }] = useAtom(authAtom);
  const authService = useService(AuthService);
  const session = authService.session;
  const status = useLiveData(session.status$);
  const isRevalidating = useLiveData(session.isRevalidating$);
  const { jumpToIndex } = useNavigateHelper();
  const [searchParams] = useSearchParams();
  const isLoggedIn = status === 'authenticated' && !isRevalidating;

  useEffect(() => {
    if (searchParams.has('code') || searchParams.has('state')) {
      session.revalidate();
    }

    if (isLoggedIn) {
      console.log('[sign-in] authenticated, navigating to / for bootstrap');
      const hasSessionCookie = document.cookie.includes('pique_session=');
      if (environment.isTauri && hasSessionCookie) {
        syncSessionToTauriVault()
          .catch(err =>
            console.error('[sign-in] vault sync failed:', err)
          )
          .finally(() => {
            window.location.href = '/';
          });
      } else {
        jumpToIndex(RouteLogic.REPLACE);
      }
    }
  }, [jumpToIndex, isLoggedIn, searchParams, session]);

  const onSignedIn = useCallback(() => {
    console.log('[sign-in] onSignedIn, navigating to / for bootstrap');
    jumpToIndex(RouteLogic.REPLACE);
  }, [jumpToIndex]);

  return (
    <SignInPageContainer>
      <AuthPanel state={state} onSignedIn={onSignedIn} />
    </SignInPageContainer>
  );
};

// Export the SignIn component directly
export const Component = SignIn;
